import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Button, Space} from 'antd';
import {StyledHeaderDetails, StyledHeaderDetailsInfo, StyledHeaderDetailsInfoSpace} from './StyledHeaderDetails';
import HeaderInformation from '../../../HeaderInformation';
import StatusLine from '../../SubscriptionsPage/StatusLine';
import {userRolesConstants, userStatusesConstants} from '../../../../constants';
import {objectHelpers, teamsHelpers} from '../../../../utils/helpers';
import {colorsStyles} from '../../../../utils/styles';

const {ACTIVE, DISABLED, EXTERNAL, PENDING} = userStatusesConstants;

const buttonProps = { size: 'middle', type: 'primary' };

const HeaderDetails = ({
  dispatch,
  data,
  enableActions = true,
  onActionClick,

  isSupport,
  teamTags,

  ...rest
}) => {
  const [t] = useTranslation(['main', 'teams']);

  const gObjProp = (key, defaultValue = '') => objectHelpers.getObjProp(data, key, defaultValue);

  const role = gObjProp('role');
  const status = gObjProp('account_status');
  const userRole = teamsHelpers.getUserRole(role);
  const isSupportRole = role === userRolesConstants.SUPPORT;

  const team = useMemo(() => {
    const teamId = data?.team_id;
    return teamTags.find(tag => tag.id === teamId)?.tag_name || teamId;
  }, [data?.team_id, teamTags]);

  const actionButtons = {
    [ACTIVE]: !isSupport && (
      <Button
        danger
        onClick={(e) => onActionClick({e, action: 'disable'})}
        {...buttonProps}
      >
        {t('disable')}
      </Button>
    ),
    [DISABLED]: !isSupport && (
      <Button
        onClick={(e) => onActionClick({e, action: 'enable'})}
        {...buttonProps}
      >
        {t('enable')}
      </Button>
    ),
    [EXTERNAL]: (
      <Button
        onClick={(e) => onActionClick({e, action: 'invite'})}
        {...buttonProps}
      >
        {t('invite')}
      </Button>
    ),
    [PENDING]: (
      <>
        <Button
          onClick={(e) => onActionClick({e, action: 'resend-invite'})}
          {...buttonProps}
        >
          {t('teams:resendInvite')}
        </Button>
        {!isSupport && (
          <Button
            danger
            onClick={(e) => onActionClick({e, action: 'delete'})}
            {...buttonProps}
          >
            {t('cancel')} {t('invitation')}
          </Button>
        )}
      </>
    ),
  };

  const getActionButtons = () => {
    let buttons = actionButtons.hasOwnProperty(status) ? actionButtons[status] : '';
    // hide actions is editor has support role && edited user has support role as well
    if (isSupportRole && !isSupport && [ACTIVE, DISABLED].includes(status)) {
      buttons = '';
    }
    return buttons;
  }

  const extra = (
    <Space size='small' className='d-flex-child'>
      <StatusLine color={status === DISABLED ? colorsStyles.functional_danger_d : userRole.color} /> {t(userRole.key)}
    </Space>
  );

  const buttons = getActionButtons();

  return (
    <StyledHeaderDetails
      {...rest}
    >
      <HeaderInformation
        className='team-user-header-details'
        description={data?.email}
        extra={extra}
        logo={data?.logo || undefined}
        name={data?.full_name}
      >
        <StyledHeaderDetailsInfoSpace size='small' direction='vertical'>
          <StyledHeaderDetailsInfo>{t('team')}: {team}</StyledHeaderDetailsInfo>
          <StyledHeaderDetailsInfo>{t('externalId')}: {data?.external_id}</StyledHeaderDetailsInfo>
        </StyledHeaderDetailsInfoSpace>
      </HeaderInformation>
      {enableActions && (
        <Space size='middle'>
          {!isSupportRole && (
            <Button
              {...buttonProps}
              onClick={(e) => onActionClick({e, action: 'edit'})}
              type='default'
            >
              {t('edit')}
            </Button>
          )}
          {buttons}
        </Space>
      )}
    </StyledHeaderDetails>
  );
}

HeaderDetails.propTypes = {
  data: PropTypes.object,
  enableActions: PropTypes.bool,
  onActionClick: PropTypes.func,
}

const mapStateToProps = ({company: {teamTags}, user: {isSupport}}) => ({isSupport, teamTags});

export default connect(mapStateToProps, null)(HeaderDetails);
